
import { getSunTimes, type SunTimes } from './sunPosition';

export interface GalacticCorePosition {
  time: Date;
  azimuth: number;   // degrees, 0=N, 90=E, 180=S, 270=W
  altitude: number;  // degrees above horizon
}

export interface GalacticCoreVisibility {
  visible: boolean;
  start: Date | null;
  end: Date | null;
  reason?: string;
  maxElevation?: number;
  durationMinutes?: number;
  // Where the core is when the dark visibility window starts and ends. That is
  // the horizon crossing only when the core rises or sets in darkness; often the
  // window opens at dusk or closes at dawn with the core already up (ERI-2395).
  riseAzimuth?: number;    // compass bearing at the start of the window
  setAzimuth?: number;     // compass bearing at the end of the window
  startAltitude?: number;  // degrees above the horizon at the start of the window
  endAltitude?: number;    // degrees above the horizon at the end of the window
  // The highest point during the dark window (not the meridian max, which can
  // fall in daylight; that is maxElevation).
  peakAzimuth?: number;    // compass bearing at the highest point in the dark
  peakAltitude?: number;   // degrees above the horizon at that point
  peakTime?: Date;         // when it gets there
  positions?: GalacticCorePosition[];  // time-stepped positions during visibility window
}

// Configuration constants
const MIN_VISIBILITY_DURATION_MINUTES = 15;
const SAMPLING_INTERVAL_MINUTES = 1;

export function calculateGalacticCoreVisibility(
  date: Date, 
  latitude: number, 
  longitude: number,
  sunTimes: SunTimes
): GalacticCoreVisibility {
  // Galactic center coordinates (Sagittarius A*)
  const galacticCenterRA = 17.7611; // hours (17h 45m 40s)
  const galacticCenterDec = -29.0078; // degrees (-29° 0' 28")
  
  // Get astronomical twilight times
  const eveningTwilight = sunTimes.night; // When astronomical darkness begins
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  const nextDayTimes = getSunTimes(nextDay, latitude, longitude);
  const morningTwilight = nextDayTimes.nightEnd; // When astronomical darkness ends
  
  if (!eveningTwilight || !morningTwilight) {
    return {
      visible: false,
      start: null,
      end: null,
      reason: 'no_twilight',
      maxElevation: 0
    };
  }

  // Check if galactic center is visible at this latitude
  const maxAltitude = calculateMaxAltitude(latitude, galacticCenterDec);

  if (maxAltitude <= 0) {
    return {
      visible: false,
      start: null,
      end: null,
      reason: 'below_horizon',
      maxElevation: maxAltitude
    };
  }

  // The core's position for this night, precessed from J2000 once (ERI-2395).
  // It moves ~0.000004 degrees in a night; per-sample precession made a full
  // year 14x slower for nothing.
  const ofDate = precessToDate(galacticCenterRA, galacticCenterDec, getJulianDate(date));

  // Calculate when galactic center is above 5 degrees during dark hours
  const visibilityWindow = calculateCoreVisibilityWindow(
    date,
    latitude,
    longitude,
    ofDate.ra,
    ofDate.dec,
    eveningTwilight,
    morningTwilight
  );

  if (!visibilityWindow.start || !visibilityWindow.end) {
    return {
      visible: false,
      start: null,
      end: null,
      reason: 'no_dark_overlap',
      maxElevation: maxAltitude,
      durationMinutes: 0
    };
  }

  // Calculate duration in minutes
  const durationMs = visibilityWindow.end.getTime() - visibilityWindow.start.getTime();
  const durationMinutes = Math.round(durationMs / (1000 * 60));

  // Check if duration meets minimum threshold
  if (durationMinutes < MIN_VISIBILITY_DURATION_MINUTES) {
    return {
      visible: false,
      start: visibilityWindow.start,
      end: visibilityWindow.end,
      reason: 'insufficient_duration',
      maxElevation: maxAltitude,
      durationMinutes: durationMinutes
    };
  }

  return {
    visible: true,
    start: visibilityWindow.start,
    end: visibilityWindow.end,
    maxElevation: maxAltitude,
    durationMinutes: durationMinutes,
    riseAzimuth: visibilityWindow.riseAzimuth,
    setAzimuth: visibilityWindow.setAzimuth,
    startAltitude: visibilityWindow.startAltitude,
    endAltitude: visibilityWindow.endAltitude,
    peakAzimuth: visibilityWindow.peakAzimuth,
    peakAltitude: visibilityWindow.peakAltitude,
    peakTime: visibilityWindow.peakTime,
    positions: visibilityWindow.positions,
  };
}

function calculateMaxAltitude(latitude: number, declination: number): number {
  const latRad = (latitude * Math.PI) / 180;
  const decRad = (declination * Math.PI) / 180;
  
  // Maximum altitude when object transits (crosses meridian)
  const maxAltRad = Math.asin(Math.sin(latRad) * Math.sin(decRad) + Math.cos(latRad) * Math.cos(decRad));
  return apparentAltitude((maxAltRad * 180) / Math.PI);
}

const POSITION_STEP_MINUTES = 15; // time steps for position array

function calculateCoreVisibilityWindow(
  date: Date,
  latitude: number,
  longitude: number,
  galacticCenterRA: number,
  galacticCenterDec: number,
  eveningTwilight: Date,
  morningTwilight: Date
) {
  let visibilityStart: Date | null = null;
  let visibilityEnd: Date | null = null;
  let riseAzimuth: number | undefined;
  let setAzimuth: number | undefined;
  let peakAzimuth: number | undefined;
  let peakAltitude = -90;
  let peakTime: Date | undefined;
  let startAltitude: number | undefined;
  let endAltitude: number | undefined;
  const positions: GalacticCorePosition[] = [];

  // Sample every minute during the dark period for better accuracy
  const stepMinutes = SAMPLING_INTERVAL_MINUTES;
  const current = new Date(eveningTwilight);
  const endTime = new Date(morningTwilight);

  // If morning twilight is before evening twilight, it's the next day
  if (endTime < eveningTwilight) {
    endTime.setDate(endTime.getDate() + 1);
  }

  let lastMinuteRecorded = -1;

  while (current <= endTime) {
    const { altitude, azimuth } = calculateGalacticCenterAltAz(
      current,
      latitude,
      longitude,
      galacticCenterRA,
      galacticCenterDec,
      true, // already precessed for this night
    );

    // Consider visible when above 0 degrees
    if (altitude >= 0) {
      if (!visibilityStart) {
        visibilityStart = new Date(current);
        riseAzimuth = azimuth;
        startAltitude = altitude;
      }
      visibilityEnd = new Date(current);
      setAzimuth = azimuth;
      endAltitude = altitude;

      // Track peak elevation
      if (altitude > peakAltitude) {
        peakAltitude = altitude;
        peakAzimuth = azimuth;
        peakTime = new Date(current);
      }

      // Record position every POSITION_STEP_MINUTES
      const minutesSinceStart = visibilityStart
        ? Math.round((current.getTime() - visibilityStart.getTime()) / 60000)
        : 0;
      if (minutesSinceStart !== lastMinuteRecorded &&
          minutesSinceStart % POSITION_STEP_MINUTES === 0) {
        positions.push({
          time: new Date(current),
          azimuth,
          altitude,
        });
        lastMinuteRecorded = minutesSinceStart;
      }
    }

    current.setMinutes(current.getMinutes() + stepMinutes);
  }

  // Always include the last visible position
  if (visibilityEnd && setAzimuth !== undefined) {
    const lastPos = positions[positions.length - 1];
    if (!lastPos || lastPos.time.getTime() !== visibilityEnd.getTime()) {
      positions.push({
        time: new Date(visibilityEnd),
        azimuth: setAzimuth,
        // The core's real height when the window closes, which is well above 0°
        // when it ends at dawn rather than at a set (ERI-2395).
        altitude: endAltitude ?? 0,
      });
    }
  }

  return {
    start: visibilityStart,
    end: visibilityEnd,
    riseAzimuth,
    setAzimuth,
    startAltitude,
    endAltitude,
    peakAzimuth,
    peakAltitude: peakTime ? peakAltitude : undefined,
    peakTime,
    positions,
  };
}

export interface AltAz {
  altitude: number;  // degrees
  azimuth: number;   // degrees, 0=N, 90=E, 180=S, 270=W
}

function calculateGalacticCenterAltAz(
  time: Date,
  latitude: number,
  longitude: number,
  galacticCenterRA: number,
  galacticCenterDec: number,
  coordsOfDate = false,
): AltAz {
  // Calculate Local Sidereal Time
  const lst = calculateLSTAtTime(time, longitude);

  // The core's position for this date, not J2000 (ERI-2395). The visibility
  // scan passes coordinates it already precessed once for the night.
  const { ra, dec } = coordsOfDate
    ? { ra: galacticCenterRA, dec: galacticCenterDec }
    : precessToDate(galacticCenterRA, galacticCenterDec, getJulianDate(time));

  // Hour angle = LST - RA
  let hourAngle = lst - ra;
  hourAngle = normalizeHours(hourAngle);

  // Convert to radians
  const latRad = (latitude * Math.PI) / 180;
  const decRad = (dec * Math.PI) / 180;
  const haRad = (hourAngle * Math.PI) / 12; // hour angle in radians

  // Calculate altitude using spherical trigonometry
  const sinAlt = Math.sin(latRad) * Math.sin(decRad) +
                 Math.cos(latRad) * Math.cos(decRad) * Math.cos(haRad);

  const altitudeRad = Math.asin(Math.max(-1, Math.min(1, sinAlt)));
  // Apparent altitude, so "up" means up in the sky you see (ERI-2395).
  const altitude = apparentAltitude((altitudeRad * 180) / Math.PI);

  // Calculate azimuth
  const cosAlt = Math.cos(altitudeRad);
  let azimuth = 0;
  if (cosAlt > 0.0001) { // avoid division by zero at zenith
    const sinAz = -Math.sin(haRad) * Math.cos(decRad) / cosAlt;
    const cosAz = (Math.sin(decRad) - Math.sin(latRad) * Math.sin(altitudeRad)) / (Math.cos(latRad) * cosAlt);
    azimuth = (Math.atan2(sinAz, cosAz) * 180) / Math.PI;
    if (azimuth < 0) azimuth += 360;
  }

  return { altitude, azimuth };
}

/** The galactic core's altitude and azimuth at an instant, in degrees (ERI-2395). */
export function galacticCoreAltAz(time: Date, latitude: number, longitude: number): AltAz {
  return calculateGalacticCenterAltAz(time, latitude, longitude, 17.7611, -29.0078);
}

/**
 * Any star's altitude and azimuth from its J2000 position, through the same
 * math as the core. The real-world check (scripts/real-world-check.ts) holds it
 * to the Naval Observatory's positions for the stars beside the core (ERI-262).
 */
export function starAltAz(time: Date, latitude: number, longitude: number, raHours: number, decDeg: number): AltAz {
  return calculateGalacticCenterAltAz(time, latitude, longitude, raHours, decDeg);
}

/** Backwards-compatible wrapper that returns only altitude */
function calculateGalacticCenterAltitude(
  time: Date,
  latitude: number,
  longitude: number,
  galacticCenterRA: number,
  galacticCenterDec: number
): number {
  return calculateGalacticCenterAltAz(time, latitude, longitude, galacticCenterRA, galacticCenterDec).altitude;
}

/**
 * Precess J2000 RA (hours) / Dec (degrees) to the date (IAU 1976, ERI-2395).
 * Sgr A*'s J2000 position drifts about 0.37 degrees by 2027; nutation and
 * aberration are a few hundredths of a degree and left out.
 */
function precessToDate(raHours: number, decDeg: number, jd: number): { ra: number; dec: number } {
  const T = (jd - 2451545.0) / 36525;
  const arcsec = Math.PI / (180 * 3600);
  const zeta = (2306.2181 * T + 0.30188 * T * T + 0.017998 * T * T * T) * arcsec;
  const z = (2306.2181 * T + 1.09468 * T * T + 0.018203 * T * T * T) * arcsec;
  const theta = (2004.3109 * T - 0.42665 * T * T - 0.041833 * T * T * T) * arcsec;
  const ra0 = (raHours * Math.PI) / 12;
  const dec0 = (decDeg * Math.PI) / 180;
  const A = Math.cos(dec0) * Math.sin(ra0 + zeta);
  const B = Math.cos(theta) * Math.cos(dec0) * Math.cos(ra0 + zeta) - Math.sin(theta) * Math.sin(dec0);
  const C = Math.sin(theta) * Math.cos(dec0) * Math.cos(ra0 + zeta) + Math.cos(theta) * Math.sin(dec0);
  let ra = ((Math.atan2(A, B) + z) * 12) / Math.PI;
  ra = ((ra % 24) + 24) % 24;
  return { ra, dec: (Math.asin(Math.max(-1, Math.min(1, C))) * 180) / Math.PI };
}

/**
 * Apparent altitude: the true altitude plus atmospheric refraction (Saemundsson,
 * 10 C, 1010 hPa), what the eye, PhotoPills and Stellarium show (ERI-2395).
 * About 0.5 degrees at the horizon, 0.03 at 30 degrees. Below -1 degree the
 * formula no longer applies and the true altitude is returned.
 */
function apparentAltitude(trueAltDeg: number): number {
  if (trueAltDeg < -1) return trueAltDeg;
  const arcmin = 1.02 / Math.tan(((trueAltDeg + 10.3 / (trueAltDeg + 5.11)) * Math.PI) / 180);
  return trueAltDeg + arcmin / 60;
}

function calculateLSTAtTime(time: Date, longitude: number): number {
  // Greenwich Mean Sidereal Time from the full Julian date, time of day
  // included (ERI-2395). The old version fed the time-of-day JD into a
  // GMST-at-0h formula and then added the hours again, counting them twice:
  // up to ~4 minutes of sidereal time, ~1 degree of hour angle.
  const d = getJulianDate(time) - 2451545.0;
  const T = d / 36525;
  const gmst = 18.697374558 + 24.06570982441908 * d + 0.000026 * T * T;
  return normalizeHours(gmst + longitude / 15);
}

function normalizeHours(hours: number): number {
  // Modulo, not a loop: sidereal time from the full Julian date is ~233,000
  // hours, and subtracting 24 at a time took ~9,700 passes per call (ERI-2395).
  return ((hours % 24) + 24) % 24;
}

function getJulianDate(date: Date): number {
  let year = date.getUTCFullYear();
  let month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  const second = date.getUTCSeconds();
  
  if (month <= 2) {
    year -= 1;
    month += 12;
  }
  
  const a = Math.floor(year / 100);
  const b = 2 - a + Math.floor(a / 4);
  
  const jd = Math.floor(365.25 * (year + 4716)) + 
             Math.floor(30.6001 * (month + 1)) + 
             day + b - 1524.5 + 
             (hour + minute / 60 + second / 3600) / 24;
  
  return jd;
}
